import React, { Fragment, forwardRef, useEffect, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

/**
 * Lock body scroll while compensating for scrollbar width to prevent layout shift.
 * Returns a cleanup function to restore original state.
 */
export function lockBodyScroll() {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  const previousOverflow = document.body.style.overflow;
  const previousPaddingRight = document.body.style.paddingRight;
  document.body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
  return () => {
    document.body.style.overflow = previousOverflow;
    // react-doctor-disable-next-line react-doctor/js-batch-dom-css
    document.body.style.paddingRight = previousPaddingRight;
  };
}

/**
 * Modal Component
 *
 * A polished, accessible modal/dialog component with smooth animations,
 * backdrop blur, and mobile-friendly bottom sheet variant.
 *
 * @example
 * <Modal isOpen={open} onClose={() => setOpen(false)}>
 *   <Modal.Header>
 *     <Modal.Title>Modal Title</Modal.Title>
 *   </Modal.Header>
 *   <Modal.Body>Content</Modal.Body>
 *   <Modal.Footer>
 *     <Button onClick={() => setOpen(false)}>Close</Button>
 *   </Modal.Footer>
 * </Modal>
 */

const modalSizes = {
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-[95vw]',
};

const Modal = forwardRef(
  (
    {
      isOpen,
      onClose,
      children,
      size = 'md',
      closeOnBackdrop = true,
      closeOnEsc = true,
      showCloseButton = true,
      initialFocus,
      className,
      mobileSheet = true, // Use bottom sheet on mobile
      ...props
    },
    ref
  ) => {
    // Lock body scroll when modal is open (with scrollbar compensation)
    useEffect(() => {
      if (isOpen) {
        return lockBodyScroll();
      }
    }, [isOpen]);

    const handleClose = useCallback(() => {
      if (onClose) onClose();
    }, [onClose]);

    // Headless UI's onClose covers Escape and backdrop together; when the
    // backdrop is locked, Escape still has to work on its own.
    useEffect(() => {
      if (!isOpen || !closeOnEsc || closeOnBackdrop) return;
      const onKeyDown = (e) => {
        if (e.key === 'Escape') handleClose();
      };
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, closeOnEsc, closeOnBackdrop, handleClose]);

    return (
      <Transition show={isOpen} as={Fragment}>
        <Dialog
          ref={ref}
          as="div"
          className="relative z-50"
          onClose={closeOnBackdrop ? handleClose : () => {}}
          initialFocus={initialFocus}
          {...props}
        >
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"
              aria-hidden="true"
            />
          </Transition.Child>

          {/* Modal container */}
          <div className="fixed inset-0 overflow-y-auto">
            <div
              className={clsx(
                'flex min-h-full items-center justify-center p-4',
                mobileSheet && 'sm:items-center items-end'
              )}
            >
              {/* Modal panel */}
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom={clsx(
                  'opacity-0',
                  mobileSheet
                    ? 'translate-y-full sm:translate-y-0 sm:scale-95'
                    : 'scale-95'
                )}
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo={clsx(
                  'opacity-0',
                  mobileSheet
                    ? 'translate-y-full sm:translate-y-0 sm:scale-95'
                    : 'scale-95'
                )}
              >
                <Dialog.Panel
                  className={clsx(
                    'relative flex w-full flex-col transform overflow-hidden',
                    'bg-white shadow-soft-xl',
                    'transition',
                    modalSizes[size],
                    // Mobile bottom sheet styles
                    mobileSheet
                      ? 'rounded-t-2xl sm:rounded-2xl max-h-[90vh] sm:max-h-[85vh]'
                      : 'rounded-2xl max-h-[85vh]',
                    className
                  )}
                >
                  {/* Mobile drag handle */}
                  {mobileSheet && (
                    <div className="sm:hidden flex justify-center pt-3 pb-1">
                      <div className="w-10 h-1 bg-gray-300 rounded-full" />
                    </div>
                  )}

                  {/* Close button */}
                  {showCloseButton && (
                    <button
                      type="button"
                      onClick={handleClose}
                      className={clsx(
                        'absolute right-4 top-4 z-10',
                        'p-1.5 rounded-lg',
                        'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-wrg-blue-500',
                        'transition-colors duration-150'
                      )}
                      aria-label="Close modal"
                    >
                      <XMarkIcon className="size-5" />
                    </button>
                  )}

                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }
);

Modal.displayName = 'Modal';

/**
 * Modal Header
 */
const ModalHeader = forwardRef(
  ({ children, className, divided = true, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'px-6 py-4',
          divided && 'border-b border-gray-100',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

ModalHeader.displayName = 'ModalHeader';

/**
 * Modal Title
 */
const ModalTitle = forwardRef(
  ({ children, className, ...props }, ref) => {
    return (
      <Dialog.Title
        ref={ref}
        as="h3"
        className={clsx(
          'text-lg font-semibold text-gray-900 pr-8',
          className
        )}
        {...props}
      >
        {children}
      </Dialog.Title>
    );
  }
);

ModalTitle.displayName = 'ModalTitle';

/**
 * Modal Description
 */
const ModalDescription = forwardRef(
  ({ children, className, ...props }, ref) => {
    return (
      <Dialog.Description
        ref={ref}
        className={clsx('text-sm text-gray-500 mt-1', className)}
        {...props}
      >
        {children}
      </Dialog.Description>
    );
  }
);

ModalDescription.displayName = 'ModalDescription';

/**
 * Modal Body
 */
const ModalBody = forwardRef(
  ({ children, className, noPadding = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'flex-1 min-h-0 overflow-y-auto scrollbar-thin',
          !noPadding && 'px-6 py-4',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

ModalBody.displayName = 'ModalBody';

/**
 * Modal Footer
 */
const ModalFooter = forwardRef(
  ({ children, className, divided = true, align = 'right', ...props }, ref) => {
    const alignClasses = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
      between: 'justify-between',
    };

    return (
      <div
        ref={ref}
        className={clsx(
          'px-6 py-4',
          divided && 'border-t border-gray-100 bg-gray-50/50',
          'flex items-center gap-3',
          alignClasses[align],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

ModalFooter.displayName = 'ModalFooter';

// Attach sub-components
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Description = ModalDescription;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

/**
 * Confirm Modal
 *
 * Pre-built confirmation dialog
 */
const ConfirmModal = forwardRef(
  (
    {
      isOpen,
      onClose,
      onConfirm,
      title,
      message,
      confirmLabel,
      cancelLabel = 'Cancel',
      variant = 'primary', // 'primary', 'danger'
      loading = false,
      ...props
    },
    ref
  ) => {
    const confirmButtonRef = useRef(null);

    return (
      <Modal
        ref={ref}
        isOpen={isOpen}
        onClose={onClose}
        size="sm"
        initialFocus={confirmButtonRef}
        {...props}
      >
        <Modal.Header divided={false}>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="text-sm text-gray-600">{message}</p>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-lg',
              'text-gray-700 hover:bg-gray-100',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400',
              'transition-colors duration-150',
              'disabled:opacity-50'
            )}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-lg',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              'transition-colors duration-150',
              'disabled:opacity-50',
              variant === 'danger'
                ? 'bg-error-500 text-white hover:bg-error-600 focus-visible:ring-error-500'
                : 'bg-wrg-blue-500 text-white hover:bg-wrg-blue-700 focus-visible:ring-wrg-blue-500'
            )}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {confirmLabel}
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
);

ConfirmModal.displayName = 'ConfirmModal';

/**
 * Alert Modal
 *
 * Pre-built alert/info dialog
 */
const AlertModal = forwardRef(
  (
    {
      isOpen,
      onClose,
      title,
      message,
      type = 'info', // 'info', 'success', 'warning', 'error'
      buttonLabel = 'OK',
      ...props
    },
    ref
  ) => {
    const icons = {
      info: (
        <div className="p-3 rounded-full bg-info-100">
          <svg className="size-6 text-info-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      ),
      success: (
        <div className="p-3 rounded-full bg-success-100">
          <svg className="size-6 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      ),
      warning: (
        <div className="p-3 rounded-full bg-warning-100">
          <svg className="size-6 text-warning-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
      ),
      error: (
        <div className="p-3 rounded-full bg-error-100">
          <svg className="size-6 text-error-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      ),
    };

    return (
      <Modal ref={ref} isOpen={isOpen} onClose={onClose} size="sm" {...props}>
        <Modal.Body className="text-center py-8">
          <div className="flex justify-center mb-4">{icons[type]}</div>
          {title && (
            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-2">
              {title}
            </Dialog.Title>
          )}
          {message && <p className="text-sm text-gray-600">{message}</p>}
        </Modal.Body>
        <Modal.Footer align="center" divided={false}>
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'px-6 py-2 text-sm font-medium rounded-lg',
              'bg-wrg-blue-500 text-white hover:bg-wrg-blue-700',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-wrg-blue-500 focus-visible:ring-offset-2',
              'transition-colors duration-150'
            )}
          >
            {buttonLabel}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
);

AlertModal.displayName = 'AlertModal';

/**
 * Drawer Component
 *
 * Side panel that slides in from the edge
 */
const Drawer = forwardRef(
  (
    {
      isOpen,
      onClose,
      children,
      position = 'right', // 'left', 'right', 'top', 'bottom'
      size = 'md',
      showCloseButton = true,
      className,
      ...props
    },
    ref
  ) => {
    const drawerSizes = {
      sm: position === 'left' || position === 'right' ? 'w-screen max-w-[18rem]' : 'h-72',
      md: position === 'left' || position === 'right' ? 'w-screen max-w-[24rem]' : 'h-96',
      lg: position === 'left' || position === 'right' ? 'w-screen max-w-[32rem]' : 'h-[32rem]',
      xl: position === 'left' || position === 'right' ? 'w-screen max-w-[40rem]' : 'h-[40rem]',
      full: position === 'left' || position === 'right' ? 'w-screen' : 'h-screen',
    };

    const positionClasses = {
      left: 'inset-y-0 left-0',
      right: 'inset-y-0 right-0',
      top: 'inset-x-0 top-0',
      bottom: 'inset-x-0 bottom-0',
    };

    const enterFrom = {
      left: '-translate-x-full',
      right: 'translate-x-full',
      top: '-translate-y-full',
      bottom: 'translate-y-full',
    };

    useEffect(() => {
      if (isOpen) {
        return lockBodyScroll();
      }
    }, [isOpen]);

    return (
      <Transition show={isOpen} as={Fragment}>
        <Dialog
          ref={ref}
          as="div"
          className="relative z-50"
          onClose={onClose}
          {...props}
        >
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"
              aria-hidden="true"
            />
          </Transition.Child>

          {/* Drawer panel */}
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-out duration-300"
                enterFrom={enterFrom[position]}
                enterTo="translate-x-0 translate-y-0"
                leave="transform transition ease-in duration-200"
                leaveFrom="translate-x-0 translate-y-0"
                leaveTo={enterFrom[position]}
              >
                <Dialog.Panel
                  className={clsx(
                    'fixed overflow-y-auto',
                    'bg-white shadow-soft-xl',
                    positionClasses[position],
                    drawerSizes[size],
                    className
                  )}
                >
                  {showCloseButton && (
                    <button
                      type="button"
                      onClick={onClose}
                      className={clsx(
                        'absolute right-4 top-4 z-10',
                        'p-1.5 rounded-lg',
                        'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-wrg-blue-500',
                        'transition-colors duration-150'
                      )}
                      aria-label="Close drawer"
                    >
                      <XMarkIcon className="size-5" />
                    </button>
                  )}
                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }
);

Drawer.displayName = 'Drawer';

// Attach sub-components to Drawer
Drawer.Header = ModalHeader;
Drawer.Title = ModalTitle;
Drawer.Description = ModalDescription;
Drawer.Body = ModalBody;
Drawer.Footer = ModalFooter;

export {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
  ConfirmModal,
  AlertModal,
  Drawer,
};
export default Modal;
